// @fastn/core — foundation types, CDM schemas, event schema, connector protocol, tenant model, RBAC types, error types
// ── Enums ──────────────────────────────────────────────────────────────────────
export var ConnectorStatus;
(function (ConnectorStatus) {
    ConnectorStatus[ConnectorStatus["draft"] = 0] = "draft";
    ConnectorStatus[ConnectorStatus["active"] = 1] = "active";
    ConnectorStatus[ConnectorStatus["deprecated"] = 2] = "deprecated";
    ConnectorStatus[ConnectorStatus["archived"] = 3] = "archived";
})(ConnectorStatus || (ConnectorStatus = {}));
export var SyncDirection;
(function (SyncDirection) {
    SyncDirection[SyncDirection["inbound"] = 0] = "inbound";
    SyncDirection[SyncDirection["outbound"] = 1] = "outbound";
    SyncDirection[SyncDirection["bidirectional"] = 2] = "bidirectional";
})(SyncDirection || (SyncDirection = {}));
export var EventType;
(function (EventType) {
    EventType[EventType["webhook"] = 0] = "webhook";
    EventType[EventType["synthetic"] = 1] = "synthetic";
    EventType[EventType["scheduled"] = 2] = "scheduled";
    EventType[EventType["manual"] = 3] = "manual";
})(EventType || (EventType = {}));
export var UserRole;
(function (UserRole) {
    UserRole["platform_admin"] = "platform_admin";
    UserRole["owner"] = "owner";
    UserRole["admin"] = "admin";
    UserRole["developer"] = "developer";
    UserRole["operator"] = "operator";
    UserRole["viewer"] = "viewer";
    UserRole["end_user"] = "end_user";
})(UserRole || (UserRole = {}));
export var WorkflowStatus;
(function (WorkflowStatus) {
    WorkflowStatus[WorkflowStatus["draft"] = 0] = "draft";
    WorkflowStatus[WorkflowStatus["active"] = 1] = "active";
    WorkflowStatus[WorkflowStatus["paused"] = 2] = "paused";
    WorkflowStatus[WorkflowStatus["failed"] = 3] = "failed";
})(WorkflowStatus || (WorkflowStatus = {}));
export var ExecutionStatus;
(function (ExecutionStatus) {
    ExecutionStatus[ExecutionStatus["pending"] = 0] = "pending";
    ExecutionStatus[ExecutionStatus["running"] = 1] = "running";
    ExecutionStatus[ExecutionStatus["success"] = 2] = "success";
    ExecutionStatus[ExecutionStatus["failed"] = 3] = "failed";
    ExecutionStatus[ExecutionStatus["retrying"] = 4] = "retrying";
})(ExecutionStatus || (ExecutionStatus = {}));
/**
 * Mailbox providers nobody owns.
 *
 * Claiming one as an org's joining domain would make every account at that
 * provider a candidate member of that org -- gmail.com as a company domain means
 * any Google user can ask to join, and auto-approve turns "ask" into "is".
 *
 * The dashboard already used a copy of this list to avoid SUGGESTING a consumer
 * mailbox, but nothing stopped someone typing one in and saving it. Shared here
 * so the suggestion and the rejection cannot disagree.
 */
export const PUBLIC_EMAIL_DOMAINS = new Set([
    "gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "hotmail.co.uk",
    "yahoo.com", "yahoo.co.uk", "yahoo.co.in", "icloud.com", "me.com", "mac.com",
    "aol.com", "protonmail.com", "proton.me", "pm.me", "live.com", "live.co.uk",
    "msn.com", "mail.com", "mail.ru", "zoho.com", "yandex.com", "yandex.ru",
    "gmx.com", "gmx.net", "gmx.de", "tutanota.com", "tuta.io", "fastmail.com",
    "hey.com", "qq.com", "163.com", "126.com", "naver.com", "web.de", "t-online.de",
    "orange.fr", "free.fr", "libero.it", "seznam.cz", "rediffmail.com",
]);
/** Is this a mailbox provider rather than an organisation's own domain? */
export function isPublicEmailDomain(domain) {
    return PUBLIC_EMAIL_DOMAINS.has(domain.trim().toLowerCase().replace(/^@/, ""));
}
/** RFC 1035 length ceiling, and the `max(253)` on POST /api/v1/orgs. */
export const MAX_ORG_DOMAIN_LENGTH = 253;
/**
 * What an organisation's joining domain has to look like to be stored.
 *
 * Dot-separated LDH labels ending in an alphabetic TLD of two or more
 * characters. This is the stricter of the two rules the API grew independently
 * (PUT /orgs/:id/settings), applied to an already-normalised value; the looser
 * one on POST /orgs accepted a numeric TLD that every later edit of the same
 * field would then refuse.
 */
export const ORG_DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;
/**
 * The single spelling of an organisation's joining domain.
 *
 * `orgs.domain` is matched with an exact `WHERE domain = $1` when a signup asks
 * to join by domain, so a stored value that is merely *equivalent* to the user's
 * email domain matches nothing: `www.acme.com` never meets `@acme.com`. Two of
 * the three writers used to normalise differently (the settings endpoint
 * lowercased, the create endpoint stored verbatim) and the third -- signup
 * auto-provisioning -- takes the domain straight off an email, so it was already
 * bare. Every writer now goes through here.
 *
 * People also paste an address, a URL, or a mixed-case hostname, all meaning one
 * domain; POST /api/v1/onboarding/agent-domain already normalised that way.
 */
export function normalizeOrgDomain(value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/^@/, "")
        .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
        .replace(/^www\./, "")
        .split(/[/?#]/)[0]
        .split("@").pop()
        .split(":")[0];
}
export var AuthType;
(function (AuthType) {
    AuthType["oauth2"] = "oauth2";
    AuthType["api_key"] = "api_key";
    AuthType["basic"] = "basic";
    AuthType["jwt"] = "jwt";
    // Added with the connector-level authType table (services/api/src/lib/ucl-hub-proxy.ts
    // AUTH_TYPE_BY_METHOD): the value a connector advertises when its default method is one of
    // these. Nothing in the SDK produces them; readers that only knew the four above should
    // treat an unknown value as a credential form, which is what they already did for api_key.
    AuthType["oauth1"] = "oauth1";
    AuthType["bearer"] = "bearer";
    AuthType["digest"] = "digest";
    AuthType["aws"] = "aws";
    AuthType["none"] = "none";
})(AuthType || (AuthType = {}));
// ── Connector Enums ───────────────────────────────────────────────────────────
export var AuthMethodType;
(function (AuthMethodType) {
    AuthMethodType["NO_AUTH"] = "NO_AUTH";
    AuthMethodType["BASIC"] = "BASIC";
    AuthMethodType["BEARER"] = "BEARER";
    AuthMethodType["JWT_BEARER"] = "JWT_BEARER";
    AuthMethodType["AWS_SIGNATURE"] = "AWS_SIGNATURE";
    AuthMethodType["OAUTH_1"] = "OAUTH_1";
    AuthMethodType["OAUTH_2"] = "OAUTH_2";
    AuthMethodType["API_KEY"] = "API_KEY";
    AuthMethodType["INPUT"] = "INPUT";
    AuthMethodType["DIGEST"] = "DIGEST";
})(AuthMethodType || (AuthMethodType = {}));
export var OAuthGrantType;
(function (OAuthGrantType) {
    OAuthGrantType["authorization_code"] = "authorization_code";
    OAuthGrantType["client_credentials"] = "client_credentials";
})(OAuthGrantType || (OAuthGrantType = {}));
export var TokenRotationStrategy;
(function (TokenRotationStrategy) {
    TokenRotationStrategy["none"] = "none";
    TokenRotationStrategy["always"] = "always";
    TokenRotationStrategy["detect"] = "detect";
})(TokenRotationStrategy || (TokenRotationStrategy = {}));
export var ConnectorProtocol;
(function (ConnectorProtocol) {
    ConnectorProtocol["REST"] = "REST";
    ConnectorProtocol["MCP"] = "MCP";
    ConnectorProtocol["FTP"] = "FTP";
    ConnectorProtocol["DB"] = "DB";
    ConnectorProtocol["REDIS"] = "REDIS";
})(ConnectorProtocol || (ConnectorProtocol = {}));
export var ActionProtocol;
(function (ActionProtocol) {
    ActionProtocol["HTTP"] = "HTTP";
    ActionProtocol["MCP"] = "MCP";
    ActionProtocol["FTP"] = "FTP";
    ActionProtocol["DB"] = "DB";
    ActionProtocol["REDIS"] = "REDIS";
})(ActionProtocol || (ActionProtocol = {}));
export var HttpMethod;
(function (HttpMethod) {
    HttpMethod["GET"] = "GET";
    HttpMethod["POST"] = "POST";
    HttpMethod["PUT"] = "PUT";
    HttpMethod["PATCH"] = "PATCH";
    HttpMethod["DELETE"] = "DELETE";
})(HttpMethod || (HttpMethod = {}));
export var HttpBodyType;
(function (HttpBodyType) {
    HttpBodyType["none"] = "none";
    HttpBodyType["json"] = "json";
    HttpBodyType["text"] = "text";
    HttpBodyType["raw"] = "raw";
    HttpBodyType["formData"] = "form-data";
    HttpBodyType["formUrlEncoded"] = "form-urlencoded";
    HttpBodyType["binary"] = "binary";
    HttpBodyType["multipartRelated"] = "multipart-related";
    HttpBodyType["graphql"] = "graphql";
})(HttpBodyType || (HttpBodyType = {}));
export var CodeLanguage;
(function (CodeLanguage) {
    CodeLanguage["PYTHON"] = "PYTHON";
    CodeLanguage["JS"] = "JS";
})(CodeLanguage || (CodeLanguage = {}));
export var RateLimitStrategy;
(function (RateLimitStrategy) {
    RateLimitStrategy["FIXED_WINDOW"] = "FIXED_WINDOW";
    RateLimitStrategy["SLIDING_WINDOW"] = "SLIDING_WINDOW";
    RateLimitStrategy["CONCURRENCY"] = "CONCURRENCY";
})(RateLimitStrategy || (RateLimitStrategy = {}));
export var ConnectionStatus;
(function (ConnectionStatus) {
    ConnectionStatus["ACTIVE"] = "ACTIVE";
    ConnectionStatus["INACTIVE"] = "INACTIVE";
    ConnectionStatus["EXPIRED"] = "EXPIRED";
    ConnectionStatus["FAILED"] = "FAILED";
})(ConnectionStatus || (ConnectionStatus = {}));
// ── OAuth Provider Types ──────────────────────────────────────────────────────
export var OAuthProviderType;
(function (OAuthProviderType) {
    OAuthProviderType["SALESFORCE"] = "salesforce";
    OAuthProviderType["GOOGLE"] = "google";
    OAuthProviderType["HUBSPOT"] = "hubspot";
    OAuthProviderType["MICROSOFT"] = "microsoft";
    OAuthProviderType["GITHUB"] = "github";
    OAuthProviderType["SLACK"] = "slack";
    OAuthProviderType["ZOOM"] = "zoom";
    OAuthProviderType["LINKEDIN"] = "linkedin";
    OAuthProviderType["DROPBOX"] = "dropbox";
    OAuthProviderType["CUSTOM"] = "custom";
})(OAuthProviderType || (OAuthProviderType = {}));
export var BillingPlanTier;
(function (BillingPlanTier) {
    BillingPlanTier["free"] = "free";
    BillingPlanTier["growth"] = "growth";
    BillingPlanTier["scale"] = "scale";
    BillingPlanTier["enterprise"] = "enterprise";
})(BillingPlanTier || (BillingPlanTier = {}));
// ── FTP/SFTP/FTPS Types ────────────────────────────────────────────────────
export var FtpProtocol;
(function (FtpProtocol) {
    FtpProtocol["FTP"] = "ftp";
    FtpProtocol["FTPS"] = "ftps";
    FtpProtocol["SFTP"] = "sftp";
})(FtpProtocol || (FtpProtocol = {}));
export var FtpOperation;
(function (FtpOperation) {
    FtpOperation["LIST"] = "list";
    FtpOperation["DOWNLOAD"] = "download";
    FtpOperation["READ_TEXT"] = "readText";
    FtpOperation["UPLOAD"] = "upload";
    FtpOperation["DELETE"] = "delete";
    FtpOperation["RENAME"] = "rename";
    FtpOperation["MKDIR"] = "mkdir";
    FtpOperation["EXISTS"] = "exists";
})(FtpOperation || (FtpOperation = {}));
export var FtpTransferMode;
(function (FtpTransferMode) {
    FtpTransferMode["BINARY"] = "binary";
    FtpTransferMode["ASCII"] = "ascii";
})(FtpTransferMode || (FtpTransferMode = {}));
// ── Database Types ────────────────────────────────────────────────────────
export var DbType;
(function (DbType) {
    DbType["POSTGRES"] = "postgres";
    DbType["REDSHIFT"] = "redshift";
    DbType["MYSQL"] = "mysql";
    DbType["MSSQL"] = "mssql";
    DbType["MONGODB"] = "mongodb";
})(DbType || (DbType = {}));
export var DbOperation;
(function (DbOperation) {
    DbOperation["QUERY"] = "query";
})(DbOperation || (DbOperation = {}));
// ── Redis Types ───────────────────────────────────────────────────────────
export var RedisOperation;
(function (RedisOperation) {
    RedisOperation["GET"] = "get";
    RedisOperation["SET"] = "set";
    RedisOperation["DEL"] = "del";
    RedisOperation["EXISTS"] = "exists";
    RedisOperation["EXPIRE"] = "expire";
    RedisOperation["TTL"] = "ttl";
    RedisOperation["KEYS"] = "keys";
    RedisOperation["INCR"] = "incr";
    RedisOperation["DECR"] = "decr";
    RedisOperation["MGET"] = "mget";
    RedisOperation["MSET"] = "mset";
    RedisOperation["HGET"] = "hget";
    RedisOperation["HSET"] = "hset";
    RedisOperation["HGETALL"] = "hgetall";
    RedisOperation["HDEL"] = "hdel";
    RedisOperation["LPUSH"] = "lpush";
    RedisOperation["RPUSH"] = "rpush";
    RedisOperation["LPOP"] = "lpop";
    RedisOperation["RPOP"] = "rpop";
    RedisOperation["LRANGE"] = "lrange";
    RedisOperation["LLEN"] = "llen";
    RedisOperation["PUBLISH"] = "publish";
    // String extras
    RedisOperation["INCRBY"] = "incrby";
    RedisOperation["SETNX"] = "setnx";
    // Hash extras
    RedisOperation["HMGET"] = "hmget";
    RedisOperation["HMSET"] = "hmset";
    RedisOperation["HEXISTS"] = "hexists";
    RedisOperation["HINCRBY"] = "hincrby";
    RedisOperation["HLEN"] = "hlen";
    // List extras
    RedisOperation["LTRIM"] = "ltrim";
    RedisOperation["LREM"] = "lrem";
    RedisOperation["LINDEX"] = "lindex";
    // Set operations
    RedisOperation["SADD"] = "sadd";
    RedisOperation["SREM"] = "srem";
    RedisOperation["SMEMBERS"] = "smembers";
    RedisOperation["SISMEMBER"] = "sismember";
    RedisOperation["SCARD"] = "scard";
    // Sorted set operations
    RedisOperation["ZADD"] = "zadd";
    RedisOperation["ZREM"] = "zrem";
    RedisOperation["ZRANGE"] = "zrange";
    RedisOperation["ZRANGEBYSCORE"] = "zrangebyscore";
    RedisOperation["ZSCORE"] = "zscore";
    RedisOperation["ZCARD"] = "zcard";
    // Key management extras
    RedisOperation["PERSIST"] = "persist";
    RedisOperation["TYPE"] = "type";
    // Server
    RedisOperation["PING"] = "ping";
})(RedisOperation || (RedisOperation = {}));
export function isFileRef(value) {
    if (!value || typeof value !== "object")
        return false;
    const obj = value;
    return (typeof obj.fileId === "string" &&
        typeof obj.key === "string" &&
        typeof obj.bucket === "string" &&
        typeof obj.size === "number" &&
        typeof obj.mime === "string" &&
        typeof obj.name === "string");
}
// ── Credits ───────────────────────────────────────────────────────────────────
//
// Credits are a DISPLAY unit only. Every backend number — the quota row, the
// ledger, the enforcement check — is in cents. This is the one sanctioned
// conversion, and it lives here because both sides of the drift it prevents
// need it: the dashboard renders `llm_cost_cents_per_month` as credits, and
// the plan catalogue in services/api advertises the same budget as credits.
//
// Those two disagreed for a while — the catalogue hardcoded 1,000 credits for
// Free against an enforced 500c (= 50 credits), and 50,000 for Growth against
// 5,000c (= 500). The advertised number is now DERIVED from the enforced cap
// rather than written out beside it, so the two cannot drift again.
/** Conversion ratio — 1 credit = 10 cents. */
export const CENTS_PER_CREDIT = 10;
/**
 * Cents → integer credits (floor). Non-positive cents return 0.
 *
 * A negative limit is the "unlimited" sentinel and must NOT be passed here —
 * it would floor to 0 and read as a zero ceiling. Callers test for < 0 first.
 */
export function centsToCredits(cents) {
    if (cents <= 0)
        return 0;
    return Math.floor(cents / CENTS_PER_CREDIT);
}
/**
 * AI credits each onboarding step grants, steps 1-4 in order. The onboarding
 * screens show these and the api grants them (ai_credit_grants), so both read
 * this one list.
 */
export const ONBOARDING_STEP_CREDITS = [20, 10, 10, 10];
export const ONBOARDING_CREDITS = ONBOARDING_STEP_CREDITS.reduce((a, b) => a + b, 0);
// ── Error registry + typed service errors (KAN-1104 → centralized errors) ──────
export { ERROR_REGISTRY, lookupError, FastnServiceError, } from "./errors/index.js";
// ── Email domain utilities ────────────────────────────────────────────────────
export { isPersonalEmail, emailDomain, hasPlusAlias, normalizeEmail, isPlusAliasBlocked, isCompetitorEmail, PERSONAL_EMAIL_DOMAINS, PLUS_ALIAS_WHITELIST_DOMAINS, COMPETITOR_DOMAIN_SLUGS, } from "./personal-email-domains.js";
// ── Alert metric catalog + delivery model ─────────────────────────────────────
export { ALERT_METRICS, ALERT_METRIC_KEYS, alertMetricDef, ALERT_CONDITIONS, ALERT_WINDOWS, alertWindowToInterval, ALERT_DELIVERY_TYPES, ALERT_CONNECTION_SENDERS, ALERT_SENDER_APPS, ALERT_MODES, WORKFLOW_SCOPABLE_METRICS, PROCESSING_ALERT_METRIC, PROCESSING_ALERT_MINUTES, parseTeamsAlertTarget, formatTeamsAlertTarget, ALERT_EMAIL_SUBJECT_MAX, alertEmailSubject, } from "./alerts.js";
export { ALERT_RULE_SNAPSHOT_KEY, ALERT_RULE_SNAPSHOT_META_FIELD, ALERT_RULE_SNAPSHOT_VERSION, encodeAlertSnapshotOrg, decodeAlertSnapshotOrg, decodeAlertSnapshotMeta, resolveAlertRuleFromSnapshot, ALERT_CODE_SPOOL_KEY, ALERT_CODE_SPOOL_MAX, ALERT_CODE_SPOOL_FIELD, encodeSpooledCodeAlert, decodeSpooledCodeAlert, alertWriteErrorIsPermanent, } from "./alert-outage.js";
export { verificationSecretRequired, verificationReadsStoredSecret, verificationSecretRequiredForConfig, sharedConnectorUrl } from "./webhook-verification.js";
// Payload templates for outbound event webhooks: the event service applies them, the api service
// validates them and the dashboard previews them, so all three share this one implementation.
export { PAYLOAD_FIELD_TYPES, PAYLOAD_TEMPLATE_EVENT_TYPES, PAYLOAD_TEMPLATE_LIMITS, selectPayloadMapping, applyPayloadMapping, renderOutboundPayload, payloadTemplateProblems, } from "./outbound-payload-template.js";
export { TRIGGER_PATH_PREFIX, buildTriggerWebhookUrl } from "./trigger-webhook-url.js";
// Connector-action retry policy and timeout — the config contract shared by the
// connector service (owns the connector-row layer), the workflow runtime (layers
// its manifest overrides on top), the engine (enforces the result) and the api
// service (validates edits). Pure logic, no I/O, so all four can depend on it.
export { DEFAULT_RETRY_POLICY, MAX_ACTION_TIMEOUT_MS, MIN_ACTION_TIMEOUT_MS, MIN_RETRY_HEADROOM_MS, normalizeRetryPolicy, resolveRetryPolicy, resolveTimeoutMs, parseRetryAfterMs, decideRetry, } from "./retry-policy.js";
// The workspace a UCL call is addressed under - see ucl-workspace.ts.
export { uclWorkspace } from "./ucl-workspace.js";
// What made a row - 'user' | 'embed' | 'api' | 'mcp' | 'agent' | 'system'. One definition,
// because the gateway derives it, every service writes it and both frontends render it; a
// second copy of the vocabulary or the derivation drifts silently - see resource-source.ts.
export { RESOURCE_SOURCES, RESOURCE_SOURCE_LABELS, UNKNOWN_SOURCE_LABEL, SOURCE_HEADER, CLIENT_HINT_HEADER, isResourceSource, parseResourceSource, authPathFromSub, deriveSource, } from "./resource-source.js";
// AGENT_TOKEN_HEADER / agentInternalToken / verifyAgentToken are deliberately NOT
// re-exported here. They need node:crypto, and this barrel is imported by the
// dashboard and the embed bundle - adding them broke `vite build` for the whole
// app, not just for new code. Server callers import "@fastn/core/resource-source-token".
// The fastn connection row -> UCL vault address rule, shared by the credential backfill (write
// side) and the api service's legacy-row verbs (read side) - see ucl-cred-scope.ts.
export { uclCredScopeFor } from "./ucl-cred-scope.js";
// The credential contract for the protocol connectors (FTP/DB/REDIS) and the
// check that enforces it. One definition, because the API replaces a protocol
// connector's auth methods with the contract built here — see
// protocol-credentials.ts.
export { ftpCredentialContract, dbCredentialContract, redisCredentialContract, protocolAuthMethod, checkRequiredCredentials, } from "./protocol-credentials.js";
// Which environment a request executes against, and the credential ceiling that
// bounds it. Shared because the api gateway (which stamps and enforces it) and
// the event dispatcher (which stamps it for triggers) must agree exactly — a
// drift here silently runs the wrong code — see execution-env.ts.
export { resolveExecutionEnv, ceilingForApiKeyMode, ceilingForApiKey, pinnedEnvOf, API_KEY_ENV_PIN_HEADER, apiKeyEnvPin, pinPermitsEnv, pinRefusalMessage, managedEnvForCeiling, EXECUTION_ENV_CEILING_HEADER, EXECUTION_ENV_REQUESTED_HEADER, parseEnvCeiling, DEFAULT_EXECUTION_ENV, TEST_EXECUTION_ENV, LIVE_EXECUTION_ENV, EXECUTION_ENV_HEADER, EXECUTION_ENV_EXPLICIT_HEADER, } from "./execution-env.js";
// Marks a response as authored by a workflow rather than by the workflow service
// failing. Shared because the workflow service stamps it and the api gateway's
// circuit breaker reads it — see execution-result-header.ts.
export { EXECUTION_RESULT_HEADER, EXECUTION_RESULT_ENGINE } from "./execution-result-header.js";
export { normalizeInviteStatus } from "./invite-status.js";
// ── Product analytics contract (see docs/plans/posthog-instrumentation.md) ────
//
// The event catalogue and the property envelope live in @fastn/core rather than
// beside either consumer, because the dashboard and the services must send the
// SAME event names with the SAME property shapes or their events cannot be
// joined — the client reports intent, the server reports outcomes, and a funnel
// spans both.
export { failureCategory, failureCategoryForStatus, isInternalEmail, ANALYTICS_EVENTS, redactSecrets, scrubErrorText, ERROR_TEXT_MAX, } from "./analytics.js";
// ── Connector requests ───────────────────────────────────────────────────────
//
// The strings the request form renders, the API validates and the migration
// CHECKs. One declaration, because a fourth invented at any one of those
// three is a row triage cannot filter for.
export { CONNECTOR_REQUEST_SOURCES, CONNECTOR_REQUEST_STATUSES, CONNECTOR_REQUEST_ACTOR_KINDS, connectorRequestActorKind, } from "./connector-request.js";
// ── Connector browse taxonomy ────────────────────────────────────────────────
//
// The categories the Connectors catalog filters by. Shared here because the api
// service resolves the key and the dashboard renders the chips, and a taxonomy
// that disagrees across those two is a filter that returns nothing.
export { CONNECTOR_CATEGORIES, CONNECTOR_CATEGORY_BY_SLUG, OTHER_CATEGORY, categoryLabel, isConnectorCategory, resolveConnectorCategory, } from "./connector-categories.js";
// ── Failure categories ───────────────────────────────────────────────────────
//
// Reporting's "What's failing" groups and the executions list's failureCategory filter: one rule
// table, classified in TS here and in SQL by @fastn/db's failureCategorySql.
export { FAILURE_CATEGORY_RULES, FAILURE_CATEGORIES, classifyFailure } from "./failure-category.js";
//# sourceMappingURL=index.js.map